import { isExternalRouter } from 'auth/cognito';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import {
    showCustomError,
    showErrorNotification,
    showNotification,
} from 'components/ShowNotification';
import { SkillsMemberModel } from 'generated-types/skills/member';
import { useExternalPicture, useExtMemberPicture } from 'hooks/skills/useSkillsMember';
import { useRef } from 'react';
import { Button, FlexboxGrid } from 'rsuite';

const ExternalProfilePicture = ({
    member,
    editAllowed,
}: {
    member: SkillsMemberModel;
    editAllowed: boolean;
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { addExtSkillsMemberPicture, deleteExtSkillsMemberPicture, refetch } =
        useExtMemberPicture({
            enabled: false,
            member_id: member?.member_id,
        });
    const hookToUseForPicture = isExternalRouter ? useExtMemberPicture : useExternalPicture;

    const { skillsMemberPicture } = hookToUseForPicture({
        enabled: true,
        member_id: member?.member_id,
    });

    const handleAddPictureButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleDeletePicture = (member_id: number) => {
        deleteExtSkillsMemberPicture.mutate(member_id, {
            onSuccess: () => {
                showNotification({ header: 'Picture deleted sucessfully.' });
                refetch();
            },
            onError: error => {
                showErrorNotification(error);
            },
        });
    };

    const handlePictureChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (file.size > 5000000) {
                showCustomError({
                    header: 'Image too big, must be under 5MB',
                });
            } else {
                const picture = new FormData();
                picture.append('profilePicture', file);

                addExtSkillsMemberPicture.mutate(picture, {
                    onSuccess: () => {
                        showNotification({ header: 'Profile updated successfully' });
                        refetch();
                    },
                    onError: error => {
                        showErrorNotification(error);
                    },
                });
            }
        }
        (event.target as any).value = null;
    };

    return (
        <FlexboxGrid.Item colspan={3} style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                {skillsMemberPicture ? (
                    <img
                        src={skillsMemberPicture}
                        style={{ maxWidth: 120, width: '100%', borderRadius: '50%' }}
                    />
                ) : null}
                {editAllowed ? (
                    <>
                        <Button
                            size="xs"
                            appearance="subtle"
                            color="green"
                            style={{ marginTop: 12, marginBottom: 5, maxWidth: 120 }}
                            onClick={handleAddPictureButtonClick}
                        >
                            {skillsMemberPicture ? 'Change picture' : 'Add picture'}
                        </Button>
                        <input
                            title="profilepicture"
                            type="file"
                            accept=".png,.jpg,.jpeg"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handlePictureChange}
                        />
                    </>
                ) : null}
                {skillsMemberPicture && editAllowed ? (
                    <ConfirmationDialog
                        handleOK={() => handleDeletePicture(member.member_id)}
                        header={'Delete Picture'}
                        message={'Are you sure you want to delete this profile picture?'}
                    >
                        <Button
                            size="xs"
                            appearance="subtle"
                            color="red"
                            style={{ marginBottom: 5, maxWidth: 120 }}
                            loading={deleteExtSkillsMemberPicture.isLoading}
                        >
                            Delete Picture
                        </Button>
                    </ConfirmationDialog>
                ) : null}
            </div>
        </FlexboxGrid.Item>
    );
};

export default ExternalProfilePicture;
